import { AxiosRequestConfig } from 'axios';
import instance, { ADMIN_PREFIX } from 'services/api';
import {
  CreateDoctorInput,
  DoctorStatusInput,
  PatientListQuery,
  PatientListResponse,
  UpdateUserInput,
  UserInput,
  UserListQuery,
  UserListResponse,
  UserListRow
} from 'types/api/users';

/**
 * GET /api/v2/admin/users — paginated user list with optional role/name
 * filters. BE applies the shared toSnake() interceptor (cycle-17) so the
 * response already matches the legacy USER_FRAGMENT snake_case shape.
 */
export const fetchUsers = async (
  query: UserListQuery,
  config?: AxiosRequestConfig
): Promise<UserListResponse> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.role) params.role = query.role;
  if (query.name) params.name = query.name;

  const res = await instance.get<{ data: UserListRow[]; total: number }>(
    `${ADMIN_PREFIX}/users`,
    { ...(config ?? {}), params: { ...(config?.params ?? {}), ...params } }
  );

  return {
    results: res.data.data ?? [],
    total: res.data.total ?? 0
  };
};

export const fetchUserById = async (
  id: string | number
): Promise<UserListRow> => {
  const res = await instance.get<UserListRow>(`${ADMIN_PREFIX}/users/${id}`);

  return res.data;
};

export const createUser = (input: UserInput) =>
  instance.post<UserListRow>(`${ADMIN_PREFIX}/users`, input);

export const createDoctor = (
  input: CreateDoctorInput,
  organizationHeader?: string
) =>
  instance.post<UserListRow>(
    `${ADMIN_PREFIX}/users/doctor`,
    input,
    organizationHeader
      ? { headers: { organization: organizationHeader } }
      : undefined
  );

export const updateUser = (id: string | number, input: UpdateUserInput) =>
  instance.put<UserListRow>(`${ADMIN_PREFIX}/users/${id}`, input);

export const sendDoctorEmail = (email: string) =>
  instance.post<{ message: string }>(`${ADMIN_PREFIX}/users/doctor-email`, {
    email
  });

export const blockUser = (id: string | number) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/users/block/${id}`);

export const deleteUser = (id: string | number) =>
  instance.delete<{ status: boolean }>(`${ADMIN_PREFIX}/users/${id}`);

export const updateAdminPassword = (id: string | number, password: string) =>
  instance.put<{ message: string }>(
    `${ADMIN_PREFIX}/users/update-password-admin/${id}`,
    { password }
  );

export const updateTranslatorPassword = (
  id: string | number,
  password: string
) =>
  instance.put<{ message: string }>(
    `${ADMIN_PREFIX}/users/update-password-translator/${id}`,
    { password }
  );

export const sendUserEmail = (input: {
  user_ids: number[];
  subject: string;
  body: string;
}) =>
  instance.post<{ message: string }>(`${ADMIN_PREFIX}/users/send-email`, input);

/**
 * PUT /api/v2/admin/doctors/:id/status — doctor profile status switch.
 * Maps the legacy UPDATE_DOCTOR_STATUS GraphQL mutation.
 */
export const updateDoctorStatus = (
  id: string | number,
  input: DoctorStatusInput
) => instance.put(`${ADMIN_PREFIX}/doctors/${id}/status`, input);

/**
 * GET /api/v2/admin/patients — rich patient list with the legacy
 * patientsList filter set. Cycle-17 reshaped the DTO to snake_case +
 * subscription filter + hydrated subscribe:{...} on each row.
 */
export const fetchPatients = async (
  query: PatientListQuery
): Promise<PatientListResponse> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.search) params.search = query.search;
  if (query.start_date) params.start_date = query.start_date;
  if (query.end_date) params.end_date = query.end_date;
  if (query.birthday_start) params.birthday_start = query.birthday_start;
  if (query.birthday_end) params.birthday_end = query.birthday_end;
  if (query.gender != null) params.gender = query.gender;
  if (query.confirm != null) params.confirm = query.confirm;
  if (query.lang) params.lang = query.lang;
  if (query.subscription != null) params.subscription = query.subscription;

  const res = await instance.get<{
    data: PatientListResponse['results'];
    total: number;
  }>(`${ADMIN_PREFIX}/patients`, { params });

  return {
    results: res.data.data ?? [],
    total: res.data.total ?? 0
  };
};
